import ReceiptIcon from '@mui/icons-material/Receipt';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

import ErrorSection from 'components/ErrorSection/ErrorSection';
import EulaAgreement from 'components/EulaAgreement/EulaAgreement';
import OnboardingTour from 'components/OnboardingTour/OnboardingTour';
import { useOnboardingTour } from 'contexts/OnboardingTourContext';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { StorageE } from 'declarations/enums/storage';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { getDecodedToken } from 'helpers/jwtToken';
import { columns } from 'pages/TicketsPage/components/ColumnPickerSettingsPopup/components/ColumnPickerSettings/mockedColumns';

import { getUsersLatestEulaAgreementStatus } from '../../api/eula';

import CardRow from './components/BannerMessages/BannerMessages';
import ColumnPickerSettingsPopup from './components/ColumnPickerSettingsPopup/ColumnPickerPopup';
import DealerCard from './components/DealerCard/DealerCard';
import DealerReport from './components/DealerReport/DealerReport';
import Header from './components/Header/Header';
import { HorizontallyCollapsibleCard } from './components/HorizontallyCollapsibleCard/HorizontallyCollapsibleCard';
import InsightsContent from './components/InsightsTabContent/InsightsContent';
import OpenNewTicketPopup from './components/OpenNewTicketPopup/OpenNewTicketPopup';
import OpenROListPopup from './components/OpenROListPopup/OpenROListPopup';
import CombinedScheduleCards from './components/ScheduleCards/CombinedScheduleCards';
import ScheduleListViewHeader from './components/ScheduleListViewHeader/ScheduleListViewHeader';
import TicketsCardView from './components/TicketsCardView/TicketsCardView';
import TicketsListView from './components/TicketsListView/TicketsListView';
import useTicketsPage from './hooks/useTicketsPage';
import {
  BannerAndCardsRowStyled,
  BannerCardsRowStyled,
  BannerDealerReportColumnStyled,
  CardsRowCollapsibleStyled,
  CardsRowLayoutWrapperStyled,
  CardsRowSectionStyled,
  ContentWrapStyled,
  DealerCardWrapStyled,
  DealerReportRowWrapStyled,
  HeaderBoxStyled,
  MessageWrapStyled,
  TableContentWrapStyled,
  TicketsPageStyled,
} from './TicketsPage.styled';

const TicketsPage: FC = (): JSX.Element => {
  const {
    ticketsPages,
    ticketsCount,
    isLoadingTickets,
    isTicketsFetchingError,
    fetchTicketsErrorMessage,
    fetchTicketsValidationErrorMessage,
    activeTab,
    isScheduleTicketList,
    setIsScheduleTicketList,
    selectedRoofTop,
    ticketListScrollContainerRef,
    ticketListScrollPosition,
    setTicketListScrollPosition,
  } = useTickets();
  const { handleSelectTicket } = useTicketsPage({ ticketsPages });
  const tableContentRef = useRef<HTMLDivElement>(null);

  const { isRunningTour, steps, handleJoyrideCallback } = useOnboardingTour();
  const { isMobile } = useResponsive();
  const [displayEulaAgreement, setDisplayEulaAgreement] = useState(false);

  const [isCardsRowExpanded, setIsCardsRowExpanded] = useState((): boolean => {
    try {
      const stored = localStorage.getItem(StorageE.CardsRowExpanded);
      if (stored !== null) return stored === 'true';
    } catch {
      // Ignore storage errors
    }
    return !isMobile;
  });
  const idToken = localStorage.getItem(StorageE.IdToken);
  const decodedToken = getDecodedToken(idToken);
  const userId = (decodedToken?.['custom:user_id'] as string) ?? null;

  useEffect((): void => {
    try {
      localStorage.setItem(StorageE.CardsRowExpanded, String(isCardsRowExpanded));
    } catch {
      // Ignore storage errors
    }
  }, [isCardsRowExpanded]);

  useEffect(() => {
    if (activeTab !== ActiveTabTypeE.Schedule) {
      setIsScheduleTicketList(false);
    }
  }, [activeTab, setIsScheduleTicketList]);

  useEffect(() => {
    if (tableContentRef.current && activeTab !== ActiveTabTypeE.Insights) {
      tableContentRef.current.scrollTop = 0;
    }
  }, [selectedRoofTop, activeTab]);

  useEffect(() => {
    const el = ticketListScrollContainerRef.current;
    if (isMobile && el && ticketListScrollPosition > 0 && !isLoadingTickets) {
      requestAnimationFrame(() => {
        el.scrollTop = ticketListScrollPosition;
        setTicketListScrollPosition(0);
      });
    }
  }, [
    isMobile,
    ticketListScrollPosition,
    setTicketListScrollPosition,
    isLoadingTickets,
    ticketListScrollContainerRef,
  ]);

  useEffect(() => {
    if (isTicketsFetchingError && userId) {
      let isMounted = true;

      getUsersLatestEulaAgreementStatus(userId)
        .then(statusResponse => {
          if (isMounted) {
            if (!statusResponse) {
              setDisplayEulaAgreement(true);
            } else {
              setDisplayEulaAgreement(false);
            }
          }
        })
        .catch(error => {
          if (isMounted) {
            // eslint-disable-next-line no-console -- intentional error logging for EULA fetch failure
            console.error('Error fetching EULA agreement:', error);
          }
        });

      return () => {
        isMounted = false;
      };
    }
  }, [isTicketsFetchingError, userId]);

  if (isTicketsFetchingError) {
    if (displayEulaAgreement) {
      return <EulaAgreement />;
    }

    return (
      <TicketsPageStyled>
        <ContentWrapStyled>
          <HeaderBoxStyled>{activeTab !== ActiveTabTypeE.Insights && <Header />}</HeaderBoxStyled>
          <MessageWrapStyled>
            <ErrorSection
              statusReason={isTicketsFetchingError ? 'fetchTickets' : null}
              errorDetails={fetchTicketsErrorMessage || fetchTicketsValidationErrorMessage}
            />
          </MessageWrapStyled>
        </ContentWrapStyled>
      </TicketsPageStyled>
    );
  }

  return (
    <TicketsPageStyled>
      <ContentWrapStyled ref={isMobile ? ticketListScrollContainerRef : undefined}>
        <HeaderBoxStyled>
          {activeTab !== ActiveTabTypeE.Insights && (
            <>
              <CardsRowSectionStyled $expanded={isCardsRowExpanded}>
                <CardsRowCollapsibleStyled $expanded={isCardsRowExpanded}>
                  <CardsRowLayoutWrapperStyled>
                    <CardRow />
                    <BannerAndCardsRowStyled>
                      <DealerCardWrapStyled $isSchedule={activeTab === ActiveTabTypeE.Schedule}>
                        <DealerCard />
                      </DealerCardWrapStyled>
                      <BannerDealerReportColumnStyled>
                        <BannerCardsRowStyled>
                          {activeTab !== ActiveTabTypeE.Schedule && (
                            <HorizontallyCollapsibleCard
                              title="Billing Periods"
                              storageKey={StorageE.BillingCardCollapsed}
                              collapsedIcon={<ReceiptIcon />}
                              disableOnMobile
                              isMobile={isMobile}
                            >
                              <DealerReportRowWrapStyled>
                                <DealerReport isReportOpened />
                              </DealerReportRowWrapStyled>
                            </HorizontallyCollapsibleCard>
                          )}
                          {activeTab === ActiveTabTypeE.Schedule && !isScheduleTicketList && (
                            <CombinedScheduleCards />
                          )}
                        </BannerCardsRowStyled>
                      </BannerDealerReportColumnStyled>
                    </BannerAndCardsRowStyled>
                  </CardsRowLayoutWrapperStyled>
                </CardsRowCollapsibleStyled>
              </CardsRowSectionStyled>
              {activeTab === ActiveTabTypeE.Schedule && isScheduleTicketList && (
                <ScheduleListViewHeader />
              )}
              <Header
                isCardsRowExpanded={isCardsRowExpanded}
                onToggleCardsRow={(): void => setIsCardsRowExpanded(!isCardsRowExpanded)}
              />
            </>
          )}
        </HeaderBoxStyled>
        {activeTab === ActiveTabTypeE.Insights ? (
          <InsightsContent />
        ) : (
          <TableContentWrapStyled ref={tableContentRef}>
            {isMobile ? (
              <TicketsCardView
                isLoadingTickets={isLoadingTickets}
                ticketsPages={ticketsPages}
                onSelectTicket={handleSelectTicket}
              />
            ) : (
              <TicketsListView
                columns={columns}
                isLoadingTickets={isLoadingTickets}
                ticketsCount={ticketsCount}
                ticketsPages={ticketsPages}
                onSelectTicket={handleSelectTicket}
              />
            )}
          </TableContentWrapStyled>
        )}
      </ContentWrapStyled>
      <OpenNewTicketPopup />
      <OpenROListPopup />
      <OnboardingTour
        hideCloseButton
        disableOverlayClose
        disableScrollParentFix
        continuous
        showSkipButton
        callback={handleJoyrideCallback}
        run={isRunningTour}
        steps={steps}
      />
      <ColumnPickerSettingsPopup />
    </TicketsPageStyled>
  );
};

export default TicketsPage;
